import React, { useEffect } from 'react'
import { Form, Input, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Modal } from '~/Components/Modal'
import { PasswordInput } from '~/Components/Forms'
import { UserItemData } from '~/Services/UserService.d'
import { useAppSelector } from '~/Store/hooks'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { UsersActions } from '~/Store/AdminUser/slice'
import { RootState } from '~/Store/reducers'

interface EditUserModalProps {
  visible: boolean
  user: UserItemData | null
  onClose: () => void
  onSuccess?: () => void
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  visible,
  user,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const updateStatus = useSelector(
    (state: RootState) => state.users.updateStatus,
  )
  const updateMessage = useSelector(
    (state: RootState) => state.users.updateMessage,
  )

  const updating = updateStatus === 'updating'

  const defaultOrg = useAppSelector(getDefaultOrganization)

  useEffect(() => {
    if (visible && user) {
      form.setFieldsValue({
        firstName: user.firstname || '',
        lastName: user.lastname || '',
        email: user.email || '',
        password: '******',
        confirmPassword: '******',
      })
    } else {
      form.resetFields()
    }
  }, [visible, user, form])

  useEffect(() => {
    if (!visible) return

    if (updateStatus === 'success') {
      message.success(t('updateUserSuccess'))
      dispatch(UsersActions.resetUpdateUser())
      form.resetFields()
      onClose()
    }

    if (updateStatus === 'fail') {
      message.error(updateMessage || t('updateFailed'))
      dispatch(UsersActions.resetUpdateUser())
    }
  }, [updateStatus, updateMessage, visible])

  const handleSubmit = async (values: any) => {
    if (!user || !defaultOrg) {
      message.error(t('cannotGetUserOrOrgInfo'))
      return
    }

    if (values.password && values.password !== values.confirmPassword) {
      message.error(t('passwordNotMatch'))
      return
    }

    const updatedUser: UserItemData = {
      ...user,
      firstname: values.firstName,
      lastname: values.lastName,
      ...(values.password ? { password: values.password } : {}),
    }

    dispatch(UsersActions.updateUsersRequest(updatedUser))
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={t('updateUserInfo')}
      visible={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={updating}
      width={500}
      okText={t('saveChanges')}
      cancelText={t('cancel')}
      loadingData={updating}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label={t('firstName')}
          name="firstName"
          rules={[
            { required: true, message: t('pleaseEnterFirstName') },
            { max: 50, message: t('firstNameMaxLength') },
          ]}
        >
          <Input placeholder={t('enterFirstName')} />
        </Form.Item>

        <Form.Item
          label={t('lastName')}
          name="lastName"
          rules={[
            { required: true, message: t('pleaseEnterLastName') },
            { max: 100, message: t('lastNameMaxLength') },
          ]}
        >
          <Input placeholder={t('enterLastName')} />
        </Form.Item>

        <Form.Item
          label={t('password')}
          name="password"
          rules={[{ min: 6, message: t('passwordMinLength') }]}
        >
          <PasswordInput
            placeholder={t('enterNewPassword')}
            onGenerate={(password) => {
              form.setFieldsValue({
                password,
                confirmPassword: password,
              })
              message.success(t('passwordGenerated'))
            }}
          />
        </Form.Item>

        <Form.Item
          label={t('confirmPassword')}
          name="confirmPassword"
          dependencies={['password']}
          hasFeedback
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const password = getFieldValue('password')
                if (!password || !value || password === value) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  new Error(t('passwordMustMatch')),
                )
              },
            }),
          ]}
        >
          <PasswordInput
            placeholder={t('reEnterPassword')}
            showGenerator={false}
            showCopy={false}
          />
        </Form.Item>

        <Form.Item
          label={t('emailAddress')}
          name="email"
          rules={[
            { type: 'email', message: t('invalidEmailFormat') },
            { required: true, message: t('emailRequired') },
          ]}
        >
          <Input disabled placeholder={t('emailCannotChange')} />
        </Form.Item>
      </Form>
    </Modal>
  )
}